/**
 * UnifiedDevPanel - Consolidated development tools in one place
 * Combines DevTools, RBAC testing, Vessel Debug, and other utilities
 */

import { devLog } from '../../utils/devLog';
import React, { useState } from 'react';
import { 
  Wrench, Database, Users, Ship, Trash2, RefreshCw, 
  AlertTriangle, Shield, Info, X, ChevronRight, Zap 
} from 'lucide-react';
import { useVessel } from '../../contexts/VesselContext';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from '../../hooks/useVesselCollections';
import VesselDebugPanel from './VesselDebugPanel';
import StorageTestPanel from './StorageTestPanel';
import { getCategory } from '../../config/maintenance/helpers/conditions';
import { seedDemoVessel } from '../../scripts/seedDemoVessel';

const UnifiedDevPanel = () => {
  const { setCurrentVessel, getCurrentVessel, vessels } = useVessel();
  // Phase 2.6: Collections disabled - will restore in Phase 3
  // const { collections, vesselId } = useVesselCollections();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('vessel');
  const [message, setMessage] = useState('');
  const [seedingDemo, setSeedingDemo] = useState(false);
  
  const currentVessel = getCurrentVessel();
  
  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const showMessage = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // Clear all data
  const clearAllData = () => {
    if (window.confirm('Clear ALL localStorage data? This cannot be undone!')) {
      localStorage.clear();
      showMessage('All data cleared! Refreshing...');
      setTimeout(() => window.location.reload(), 1000);
    }
  };

  // Clear crew data only
  const clearCrewData = () => {
    localStorage.removeItem('seaready.crew.v1');
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('crewMembers')) {
        localStorage.removeItem(key);
      }
    });
    showMessage('Crew data cleared!');
    setTimeout(() => window.location.reload(), 1000);
  };

  // Phase 2.6: Demo data loading temporarily disabled - will restore in Phase 3
  // Load demo data for Step 4 screenshots
  const loadDemoData = async () => {
    // Phase 2.6: Collections disabled - functionality temporarily unavailable
    console.warn('Phase 2.6: Demo data loading disabled - will return in Phase 3');
    showMessage('Demo data loading temporarily disabled during migration');

    // if (!vesselId || !collections) {
    //   showMessage('No vessel selected or collections not ready');
    //   return;
    // }

    // setSeedingDemo(true);
    // try {
    //   await seedDemoVessel(collections, vesselId);
    //   showMessage('Demo data loaded! Navigate to Equipment to see results.');
    //   // Auto-navigate to Equipment after seeding
    //   setTimeout(() => {
    //     window.location.hash = '#/maintenance';
    //     // Then go to Equipment tab
    //     setTimeout(() => {
    //       window.location.hash = '#/equipment';
    //     }, 500);
    //   }, 1500);
    // } catch (error) {
    //
    //   showMessage(`Demo seed failed: ${error.message}`);
    // } finally {
    //   setSeedingDemo(false);
    // }
  };

  // Test category changes
  const testCategoryChange = (newCategory) => {
    if (!currentVessel) return;
    
    const updates = {
      category: newCategory,
      areaCategory: newCategory, // Mirror for legacy
      updatedAt: Date.now()
    };
    
    // Update vessel in context
    const updateVessel = window.__updateVessel || 
      (vessels.find(v => v.id === currentVessel.id) ? 
        (id, data) => {
          const updatedVessels = vessels.map(v => 
            v.id === id ? { ...v, ...data } : v
          );
          localStorage.setItem('vessels', JSON.stringify(updatedVessels));
          window.location.reload();
        } : null);
    
    if (updateVessel) {
      updateVessel(currentVessel.id, updates);
      showMessage(`Changed to Category ${newCategory.replace('cat', '')}`);
    }
  };

  // Run maintenance test
  const runMaintenanceTest = () => {
    if (!window.__getMaintenanceRequirementsNew) {
      showMessage('Maintenance function not available');
      return;
    }
    
    const testResults = [];
    ['cat0', 'cat1', 'cat2', 'cat3', 'cat4', 'cat5', 'cat6'].forEach(cat => {
      const testVessel = { ...currentVessel, category: cat, areaCategory: cat };
      const reqs = window.__getMaintenanceRequirementsNew(testVessel);
      testResults.push(`${cat}: ${reqs?.length || 0} items`);
    });
    
    devLog('Maintenance Test Results:', testResults);
    showMessage('Check console for results');
  };

  // Run parity suite
  const runParitySuite = () => {
    if (typeof window.__runMaintenanceParitySuite === 'function') {
      console.clear();
      devLog('Running Maintenance Parity Suite…');
      const results = window.__runMaintenanceParitySuite();
      window.__lastParityResults = results;
      showMessage('Parity suite complete! Check console.');
    } else {
      // Try to load it
      import('../../devtools/parityHarness').then(() => {
        if (typeof window.__runMaintenanceParitySuite === 'function') {
          console.clear();
          devLog('Running Maintenance Parity Suite…');
          const results = window.__runMaintenanceParitySuite();
          window.__lastParityResults = results;
          showMessage('Parity suite complete! Check console.');
        } else {
          showMessage('Parity harness not available');
        }
      }).catch(err => {
        showMessage('Error loading parity harness');
      });
    }
  };

  // Enable Key Doctor
  const enableKeyDoctor = () => {
    window.ENABLE_KEY_DOCTOR = '1';
    localStorage.setItem('ENABLE_KEY_DOCTOR', '1');
    showMessage('Key Doctor enabled! Refreshing...');
    setTimeout(() => window.location.reload(), 1000);
  };

  // Set RBAC role
  const setRole = (role) => {
    sessionStorage.setItem('userRole', role);
    showMessage(`Role changed to ${role}`);
    setTimeout(() => window.location.reload(), 500);
  };

  const currentRole = sessionStorage.getItem('userRole') || 'fleet';

  return (
    <>
      {/* Floating button - bottom right, blue to distinguish from purple RBAC */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-4 bottom-20 z-[99999] px-4 py-2 rounded-lg shadow-lg bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
        title="Dev Tools"
      >
        <Wrench className="h-5 w-5" />
        <span className="text-sm font-medium">Dev Tools</span>
      </button>

      {/* Main panel */}
      {isOpen && (
        <div className="fixed inset-y-0 right-0 z-[99999] bg-white shadow-2xl flex" style={{ width: '400px' }}>
          {/* Sidebar tabs */}
          <div className="w-12 bg-gray-900 flex flex-col">
            <button
              onClick={() => setActiveTab('vessel')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'vessel' ? 'bg-blue-600' : ''}`}
              title="Vessel Debug"
            >
              <Ship className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('maintenance')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'maintenance' ? 'bg-blue-600' : ''}`}
              title="Maintenance Test"
            >
              <Wrench className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('rbac')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'rbac' ? 'bg-blue-600' : ''}`}
              title="RBAC Testing"
            >
              <Shield className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('storage')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'storage' ? 'bg-blue-600' : ''}`}
              title="Storage Tests"
            >
              <Database className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('data')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'data' ? 'bg-blue-600' : ''}`}
              title="Data Management"
            >
              <Trash2 className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('tools')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'tools' ? 'bg-blue-600' : ''}`}
              title="Other Tools"
            >
              <Info className="h-5 w-5 text-white" />
            </button>
            <button
              onClick={() => setActiveTab('demo')}
              className={`p-3 hover:bg-gray-800 ${activeTab === 'demo' ? 'bg-blue-600' : ''}`}
              title="Demo Data (Step 4)"
            >
              <Zap className="h-5 w-5 text-white" />
            </button>
          </div>

          {/* Content area */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'vessel' && 'Vessel Debug'}
                {activeTab === 'maintenance' && 'Maintenance Testing'}
                {activeTab === 'rbac' && 'RBAC Testing'}
                {activeTab === 'storage' && 'Storage Tests'}
                {activeTab === 'data' && 'Data Management'}
                {activeTab === 'tools' && 'Other Tools'}
                {activeTab === 'demo' && 'Demo Data (Step 4)'}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Message bar */}
            {message && (
              <div className="px-6 py-2 bg-green-50 border-b border-green-200">
                <p className="text-sm text-green-800">{message}</p>
              </div>
            )}

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Vessel Debug Tab */}
              {activeTab === 'vessel' && currentVessel && (
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">Current Vessel</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span>ID:</span>
                        <span className="font-mono">{currentVessel.id}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Name:</span>
                        <span>{currentVessel.name || currentVessel.vesselName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Category:</span>
                        <span className="font-mono font-bold">{currentVessel.category}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Computed Cat:</span>
                        <span className="font-mono font-bold">{getCategory(currentVessel)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-700">Quick Category Change</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {['cat0', 'cat1', 'cat2', 'cat3', 'cat4', 'cat5', 'cat6'].map(cat => (
                        <button
                          key={cat}
                          onClick={() => testCategoryChange(cat)}
                          className={`px-3 py-2 text-sm rounded ${
                            currentVessel.category === cat 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          Cat {cat.replace('cat', '')}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="bg-orange-50 rounded-lg p-4">
                    <h4 className="font-semibold text-orange-900 mb-2">Expected Maintenance</h4>
                    <div className="text-sm space-y-1">
                      <div>Cat 0: 40 items (+MF, +Radar, +NAVTEX)</div>
                      <div>Cat 1: 39 items (+Radar, +NAVTEX)</div>
                      <div>Cat 2: 38 items (+Radar, +NAVTEX)</div>
                      <div>Cat 3-6: 36 items (base only)</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Storage Tests Tab */}
              {activeTab === 'storage' && (
                <StorageTestPanel />
              )}

              {/* Maintenance Testing Tab */}
              {activeTab === 'maintenance' && (
                <div className="space-y-4">
                  <button
                    onClick={runMaintenanceTest}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Run Category Test (All Categories)
                  </button>

                  <button
                    onClick={runParitySuite}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Run Parity Suite (Full Matrix)
                  </button>
                  
                  <button
                    onClick={() => {
                      if (currentVessel?.id) {
                        localStorage.removeItem(`maintenance_history_${currentVessel.id}`);
                        // FT-86: custom_maintenance_jobs removed - fully migrated to maintenance_jobs
                        setMessage('Maintenance cache cleared! Please refresh the page.');
                        devLog(`[Dev] Cleared maintenance cache for vessel ${currentVessel.id}`);
                      }
                    }}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  >
                    Clear Maintenance Cache
                  </button>
                  
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Testing Tools</h4>
                    <div className="text-sm space-y-1">
                      <div><strong>Category Test:</strong> Tests current vessel across all categories</div>
                      <div><strong>Parity Suite:</strong> Compares legacy vs new across vessel matrix</div>
                      <div>Results appear in console (F12)</div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <p>The Parity Suite tests maintenance requirements across multiple vessel configurations.</p>
                    <p className="mt-2">Open browser console to see detailed results.</p>
                  </div>
                </div>
              )}

              {/* RBAC Testing Tab */}
              {activeTab === 'rbac' && (
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h4 className="font-semibold text-purple-900 mb-2">Current Role</h4>
                    <p className="text-lg font-mono">{currentRole.toUpperCase()}</p>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-medium text-gray-700">Switch Role</h4>
                    <div className="space-y-2">
                      {['fleet', 'skipper', 'crew'].map(role => (
                        <button
                          key={role}
                          onClick={() => setRole(role)}
                          className={`w-full px-4 py-2 text-sm rounded ${
                            currentRole === role
                              ? 'bg-purple-600 text-white'
                              : 'bg-gray-100 hover:bg-gray-200'
                          }`}
                        >
                          {role.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <h4 className="font-medium mb-2">Role Permissions:</h4>
                    <ul className="space-y-1">
                      <li><strong>FLEET:</strong> Full control, all vessels</li>
                      <li><strong>SKIPPER:</strong> Operational control</li>
                      <li><strong>CREW:</strong> View and complete tasks</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Data Management Tab */}
              {activeTab === 'data' && (
                <div className="space-y-3">
                  <button
                    onClick={clearAllData}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center justify-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear ALL Data
                  </button>

                  <button
                    onClick={clearCrewData}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 flex items-center justify-center gap-2"
                  >
                    <Users className="h-4 w-4" />
                    Clear Crew Only
                  </button>

                  <button
                    onClick={enableKeyDoctor}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    Enable Key Doctor (Storage Scanner)
                  </button>

                  <button
                    onClick={() => {
                      localStorage.setItem('ENABLE_DEVTOOLS', '1');
                      showMessage('DevTools enabled permanently');
                    }}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Enable Legacy DevTools
                  </button>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-700 mb-2">Storage Info</h4>
                    <div className="text-sm text-gray-600">
                      <div>Items: {localStorage.length}</div>
                      <div>Size: ~{Math.round(JSON.stringify(localStorage).length / 1024)}KB</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Other Tools Tab */}
              {activeTab === 'tools' && (
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">Existing Dev Tools</h4>
                    <div className="text-sm space-y-2">
                      <div>
                        <strong>Key Doctor:</strong> Storage scanner with read-only analysis<br/>
                        <span className="text-xs">Enable with button in Data tab or ?doctor=1 in URL</span>
                      </div>
                      <div className="mt-2">
                        <strong>Parity Harness:</strong> Maintenance requirements tester<br/>
                        <span className="text-xs">Access via Maintenance tab</span>
                      </div>
                      <div className="mt-2">
                        <strong>RBAC Panel:</strong> Role testing (purple button)<br/>
                        <span className="text-xs">Already visible on screen</span>
                      </div>
                    </div>
                  </div>

                  <div className="text-sm text-gray-600">
                    <h4 className="font-medium mb-2">Console Commands:</h4>
                    <ul className="space-y-1 font-mono text-xs">
                      <li>window.__debugVesselKey(vessel)</li>
                      <li>window.__getMaintenanceRequirementsNew(vessel)</li>
                      <li>window.__runMaintenanceParitySuite()</li>
                      <li>window.testCategoryMaintenance()</li>
                      <li>window.SeaReady.conditions.getCategory(vessel)</li>
                    </ul>
                  </div>

                  <div className="pt-4 border-t">
                    <h4 className="font-medium text-gray-700 mb-2">Test Files:</h4>
                    <div className="text-sm text-gray-600">
                      <div>/test-category-maintenance.js</div>
                      <div>/debug-maintenance-load.js</div>
                      <div>/src/devtools/parityHarness.js</div>
                      <div>/src/tools/keyDoctor/*</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Demo Data Tab (Step 4) */}
              {activeTab === 'demo' && (
                <div className="space-y-4">
                  <div className="bg-amber-50 rounded-lg p-4">
                    <h4 className="font-semibold text-amber-900 mb-2">GPT Step 4: Demo Data</h4>
                    <p className="text-sm text-amber-800 mb-4">
                      Load sales-ready demo data for taking screenshots. Creates equipment with overdue/due soon statuses, 
                      drills, procedures, training records, and triggers requirements updates.
                    </p>
                    
                    <button
                      onClick={loadDemoData}
                      disabled={seedingDemo}
                      className="w-full px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Zap className="h-4 w-4" />
                      {seedingDemo ? 'Loading Demo Data...' : 'Load Demo Data'}
                    </button>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2">What Gets Created:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Fire Extinguisher</strong> - Overdue (red badge)</li>
                      <li>• <strong>Liferaft</strong> - Due soon (yellow badge)</li>
                      <li>• <strong>VHF Radio</strong> - OK status</li>
                      <li>• <strong>Maintenance Job</strong> - Service Liferaft (open)</li>
                      <li>• <strong>Fire Drill</strong> - Overdue record</li>
                      <li>• <strong>Sample Procedures</strong> - Man Overboard</li>
                      <li>• <strong>Training Record</strong> - Vessel Induction</li>
                      <li>• <strong>Inspection</strong> - Engine room housekeeping</li>
                      <li>• <strong>Requirements Update</strong> - Forces "old" hash to show badge</li>
                    </ul>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-semibold text-green-900 mb-2">Next: Take Screenshots</h4>
                    <ol className="text-sm text-green-800 space-y-1">
                      <li>1. <strong>Requirements Diff</strong> - Click "Sync Requirements" in any module</li>
                      <li>2. <strong>Equipment Status</strong> - View Equipment list with badges</li>
                      <li>3. <strong>Audit Trail</strong> - Open Data Management &gt; Audit Trail</li>
                    </ol>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnifiedDevPanel;